import { useState } from 'react';
import { z } from 'zod';
import { motion } from 'framer-motion';
import PageTransition from '../components/Motion/PageTransition';

/**
 * Contact Page - Contact form and information with smooth animations
 * User Story 3 (P3): Initiate Contact
 * Respects prefers-reduced-motion via Framer Motion's built-in support
 */

// Zod validation schema
const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(80),
  email: z.string().email('Please enter a valid email address'),
  message: z.string().min(20, 'Message must be at least 20 characters').max(1000),
  consent: z.boolean().refine((val) => val === true, 'You must accept to continue'),
});

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setStatus('loading');

    try {
      // Validate form data
      contactSchema.parse(formData);

      // Call backend API
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setStatus('success');
      setFormData({ name: '', email: '', message: '', consent: false });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formErrors = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            formErrors[err.path[0]] = err.message;
          }
        });
        setErrors(formErrors);
      } else {
        console.error('Error submitting form:', error);
        // Set general error message for network or server errors
        setErrors({
          general: error.message || 'Unable to connect to the server. Please ensure the backend is running and try again.'
        });
      }
      setStatus('error');
    }
  };

  return (
    <PageTransition>
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              variants={itemVariants}
            >
              Contact Us
            </motion.h1>
            <motion.p
              className="text-xl text-gray-600 mb-12"
              variants={itemVariants}
            >
              Ready to transform your data? Get in touch with our team today.
            </motion.p>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Send us a message</h2>

                {status === 'success' ? (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
                    <p className="font-medium">Thank you for your message!</p>
                    <p className="text-sm mt-1">
                      We'll get back to you within 1-2 business days.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        aria-invalid={errors.name ? 'true' : 'false'}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="mt-1 text-sm text-red-600">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="mt-1 text-sm text-red-600">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                          errors.message ? 'border-red-500' : 'border-gray-300'
                        }`}
                        aria-invalid={errors.message ? 'true' : 'false'}
                        aria-describedby={errors.message ? 'message-error' : undefined}
                      />
                      {errors.message && (
                        <p id="message-error" className="mt-1 text-sm text-red-600">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    {/* Consent */}
                    <div>
                      <label className="flex items-start">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleChange}
                          className="mt-1 mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-600">
                          I agree to be contacted about my inquiry *
                        </span>
                      </label>
                      {errors.consent && (
                        <p className="mt-1 text-sm text-red-600">{errors.consent}</p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full bg-primary text-white py-3 px-6 rounded-md font-medium hover:bg-primary-600 focus-visible-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {status === 'loading' ? 'Sending...' : 'Send Message'}
                    </button>

                    {status === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                        <p className="text-sm">
                          {errors.general || 'Something went wrong. Please try again later.'}
                        </p>
                      </div>
                    )}
                  </form>
                )}
              </motion.div>

              {/* Contact Information */}
              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Get in touch</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Email</h3>
                    <a
                      href="mailto:info@realvaluegroup.io"
                      className="text-primary hover:underline focus-visible-ring"
                    >
                      info@realvaluegroup.io
                    </a>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Office</h3>
                    <address className="not-italic text-gray-600">
                      <p>Bergstraat</p>
                      <p>22/303, 3110</p>
                      <p>Rotselaar, Belgium </p>
                    </address>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Connect</h3>
                    <a
                      href="https://linkedin.com/company/realvaluegroup"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary hover:underline focus-visible-ring"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                      LinkedIn
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </PageTransition>
  );
}

export default Contact;

